import {map} from 'rxjs/operators';
import {AfterViewInit, Component, forwardRef, Input, OnDestroy, OnInit, Optional, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CompleterCmp, CompleterItem, LocalData} from 'ng2-completer';
import {TranslateService} from '@ngx-translate/core';
import {GbgrundbuchamtService, GrundbuchamtId, GrundbuchamtListEntry, GrundbuchamtResponse} from '../../../core/gb-api';
import {languages_name, translateName} from '../../functions/language.functions';
import {GbAmtSearchMode} from "./gbAmtSearchMode";
import {AppRestCompleterService} from "../../../core/services/app-rest-completer.service";
import {CompleterGrundbuchamtListEntry} from './CompleterGrundbuchamtListEntry';
import {TranslateNamePipe} from "../../pipes/simple/translate-name.pipe";
import {FormatConfigService} from "../../../core/services/format-config.service";
import {Subscription} from "rxjs";
import {ComparatorService} from "../../services/comparator-service";
import {CapFormControlComponent} from 'app/shared/form-control/form-control.component';

@Component({
  selector: 'cap-gb-amt',
  templateUrl: './gb-amt.component.html',
  styleUrls: ['./gb-amt.component.scss'],
  host: {
    "class": "form-control-component"
  },
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => GbAmtComponent), multi: true},
    AppRestCompleterService,
  ]
})
export class GbAmtComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {

  @Input()
  searchMode: GbAmtSearchMode;

  @Input()
  nurGueltige = false;

  @Input()
  hasfocus = false;

  @ViewChild(CompleterCmp, {static: true})
  completer: CompleterCmp;

  @Input()
  placeholder;

  public isOpen = false;
  completerData: LocalData;
  value = '';

  propagateChange: (gbAmtId: string) => void;
  propagateTouch: (_) => void;


  private reset = false;
  private searchFields: string;
  private amtList: Array<GrundbuchamtListEntry> = [];
  private completerServiceInitialized = false;

  private subscription: Subscription = new Subscription();

  // Das Initialisieren des completerService setzt über das Event DataSourceChanged das value zurück

  constructor(private completerService: AppRestCompleterService,
              private translate: TranslateService,
              private translateNamePipe: TranslateNamePipe,
              private gbGrundbuchamtService: GbgrundbuchamtService,
              private formatConfigService: FormatConfigService,
              private comparatorService: ComparatorService,
              @Optional() private formControlComponent: CapFormControlComponent) {
  }

  ngAfterViewInit() {
    if (this.formControlComponent) {
      this.formControlComponent.registerChild(this.completer.ctrInput);
    }
  }

  ngOnInit() {
    this.resetCompleterService();
    this.subscription.add(this.translate.onLangChange.subscribe(e => this.updateCompleterService(e.lang)));
  }

  public focus() {
    this.completer.focus();
  }

  resetCompleterService() {
    const findAll = this.gbGrundbuchamtService.loadGrundbuchamtAll('RECHTE_REGISTER_SEARCH', this.nurGueltige).pipe(
      map(response => {
          this.amtList = this.changeObject(response);
          if (this.amtList.length === 1) {
            const amtListElement = this.amtList[0];
            const amt = this.translateNamePipe.transform(amtListElement.name) + ' ' + amtListElement.nummer;
            const selectedItem = {title: amt, originalObject: amtListElement};
            this.completer.completer.onSelected(selectedItem);
            this.onSelected(selectedItem);
          }
          return this.amtList;
        }
      ));
    this.searchFields = languages_name[this.translate.currentLang].name + ',nummer,title';
    this.completerServiceInitialized = true;
    this.completerData = this.completerService.local(findAll, this.searchFields, 'title');
  }

  private updateCompleterService(lang: string) {
    this.searchFields = languages_name[lang].name + ',nummer,title';
    this.completerData.titleField('title').searchFields(this.searchFields);
    this.completerData.dataSourceChange.emit();
  }

  writeValue(id: GrundbuchamtId) {
    if (id) {
      this.subscription.add(this.gbGrundbuchamtService.loadGrundbuchamtListEntry(id.id).subscribe(amt => {
        this.value = translateName(amt.name, this.translate) + ' ' + amt.nummer;
        const selectedItem = {title: this.value, originalObject: amt};
        this.completer.completer.onSelected(selectedItem);
        this.onSelected(selectedItem);
        this.reset = false;
      }));
    } else if (!this.reset) {
      this.reset = true;
      this.value = '';
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  public onOpened(isOpen: boolean) {
    this.isOpen = isOpen;
  }

  onClick() {
    if (this.completer.disableInput === true) {
      this.completer.close();
    } else {
      if (this.isOpen) {
        this.value = '';
        this.propagateChange(null);
      }
    }
  }

  onSelected(selectedItem: CompleterItem) {
    if (selectedItem) {
      this.value = selectedItem.title;
      this.propagateChange(selectedItem.originalObject.id);
      this.reset = false;
    } else {
      if (!this.completerServiceInitialized) {
        this.value = '';
        this.propagateChange(null);
      } else {
        this.completerServiceInitialized = false;
      }
    }
  }

  private changeObject(response: GrundbuchamtResponse) {
    const amtList = this.sort(response);
    const tempArray: Array<CompleterGrundbuchamtListEntry> = [];
    for (const entry of amtList) {
      tempArray.push(this.changeDatatype(entry));
    }
    return tempArray;
  }

  private sort(response: GrundbuchamtResponse) {
    const list = response.grundbuchamtList;
    const currentLang = this.translate.currentLang;
    const sortParam = <any>this.formatConfigService.parameterConfig.parameters.GRUNDBUCHAMTSORTIERUNGINTERCAPI;
    switch (sortParam) {
      case 'ALPHABETISCH_AUFSTEIGEND':
        return this.sortByComparator(currentLang, list, (a, b) => this.comparatorService.orderByComparator(a, b));
      case 'ALPHABETISCH_ABSTEIGEND':
        return this.sortByComparator(currentLang, list, (a, b) => this.comparatorService.orderByComparator(b, a));
      case 'AMTS_NUMMER_AUFSTEIGEND':
        return list.sort((a, b) => this.comparatorService.orderByComparator(a.nummer, b.nummer));
      case 'AMTS_NUMMER_ABSTEIGEND':
        return list.sort((a, b) => this.comparatorService.orderByComparator(b.nummer, a.nummer));
      default:
        return list;
    }
  }

  public emptyCompleter() {
    this.completer.value = "";
    this.value = "";
    this.onSelected(null);
  }

  private sortByComparator(currentLang: string, list: GrundbuchamtListEntry[], sort: (a: string, b: string) => number) {
    return list.sort((a, b) => sort(this.translateNamePipe.transform(a.name), this.translateNamePipe.transform(b.name)));
  }

  private changeDatatype(entry: GrundbuchamtListEntry): CompleterGrundbuchamtListEntry {
    const completerEntry: CompleterGrundbuchamtListEntry = <CompleterGrundbuchamtListEntry>{...entry, title: ''};
    let title = this.translateNamePipe.transform(entry.name);
    if (completerEntry.nummer) {
      title += ' ' + completerEntry.nummer;
    }
    completerEntry.title = title;
    return completerEntry;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

}
